/**
 * Public navigation model.
 *
 * Turns the CMS-generated nav tree into something renderable: labels resolved
 * for the active locale, and children resolved for each node.
 *
 * Child resolution has two sources, matching how an editor thinks about it:
 *
 *  - explicit children the operator added by hand, or
 *  - when a node is a `collection` item with no children of its own, the
 *    latest N entries of the linked content type. This is what the old
 *    hardcoded `ArticlesDropdown` did, now expressed as data.
 *
 * An explicit child always wins over the auto-derived list, so an operator can
 * pin specific links and still fall back to "latest items" for the rest.
 *
 * ONE EXCEPTION (2026-11-03): the articles entry is a plain link to
 * /articles. It used to be a `collection` node whose dropdown listed the
 * latest three posts; the operator asked for it to go straight to the
 * articles page instead. See `isArticlesNode`.
 *
 * Kept as pure functions (no React) so the locale is injected rather than read
 * from a hook — that keeps this testable and lets the Navbar and the Footer
 * share one resolution pass.
 */

import { cmsNav } from '@/content/cms/nav.generated'
import { NAV_MAX_DEPTH, type NavItem, type NavSourceFilter } from '@/content/nav-types'
import { cmsArticles } from '@/content/cms/articles.generated'
import type { CmsArticleIndexEntry } from '@/content/cms/articles.generated'
import { cmsMediaIndex } from '@/content/cms/media.generated'
import type { CmsMediaIndexEntry } from '@/content/cms/media.generated'
import { cmsMediaSrcSet } from '@/utils/cmsMediaUrl'
import { uuidFromUploadPath } from '@/utils/mediaPath'
import { cmsMagazines } from '@/content/cms/magazines.generated'
import type { CmsMagazineIndexEntry } from '@/content/cms/magazines.generated'
import { cmsLectures } from '@/content/cms/lectures.generated'
import type { CmsLectureIndexEntry } from '@/content/cms/lectures.generated'

export type NavLocale = 'zh-Hant' | 'zh-Hans' | 'en'

/** CMS data fetched at runtime (or prerender time) for nav resolution.
 *  When omitted, the build-time snapshots are used. */
export interface NavData {
  articles?: CmsArticleIndexEntry[]
  lectures?: CmsLectureIndexEntry[]
  magazines?: CmsMagazineIndexEntry[]
  mediaIndex?: Record<string, CmsMediaIndexEntry>
}

/**
 * One resolved menu entry, at any depth.
 *
 * The tree is recursive: an entry's children are entries. The header shows
 * up to NAV_MAX_DEPTH + 1 levels (depth 0, 1, 2), and this type is what
 * carries them.
 */
export interface ResolvedNavNode {
  /** Stable across renders; ids come from the CMS. */
  key: string
  label: string
  href: string
  isExternal: boolean
  openNewTab: boolean
  children: ResolvedNavNode[]
  /** True when THIS entry's children were auto-derived from a content
   *  collection rather than authored in the CMS — the admin shows these
   *  differently and the Navbar may show a "view all" link. */
  autoChildren: boolean
  /** Where the "view all" link for an auto-derived submenu should point. */
  collectionHref: string | null
  /** Present for collection children so the dropdown can show a thumbnail. */
  thumb?: string
  /**
   * The `srcset` for the same thumbnail.
   *
   * The dropdown draws the thumbnail in a 48px box, so the browser can pick
   * the 320px rung instead of the 1024px one — which is what the plain
   * `thumb` URL used to cost a visitor on a menu hover.
   */
  thumbSrcSet?: string
}

/** The top-level entries are the same shape as any other level. */
export type ResolvedNavItem = ResolvedNavNode

/**
 * The header as it shipped, used when the CMS has nothing to say.
 *
 * If `cmsNav` comes back empty — an operator deleted every item and has not
 * deployed a fix yet, or a build ran with SKIP_CMS_SYNC against a stale file —
 * the public site would render with no navigation at all. Falling back to this
 * list is the single most important guard in the CMS-driven nav: the failure
 * mode is "the old menu is back", not "the site has no header".
 *
 * Note the articles entry is a plain link, like every other entry — it lost
 * its auto "latest 3" dropdown on 2026-11-03.
 */
const DEFAULT_NAV_SEEDS: Array<{
  key: string
  labelKey: string
  href: string
}> = [
  { key: 'about', labelKey: 'nav.about', href: '/about' },
  { key: 'products', labelKey: 'nav.products', href: '/products' },
  { key: 'magazine', labelKey: 'nav.magazine', href: '/magazine' },
  { key: 'lectures', labelKey: 'nav.lectures', href: '/lectures' },
  { key: 'articles', labelKey: 'nav.articles', href: '/articles' },
  { key: 'contact', labelKey: 'nav.contact', href: '/contact' },
]

export type Translate = (key: string) => string

/** Operator label for this locale, else the i18n key's translation. */
function resolveLabel(
  label: Partial<Record<NavLocale, string>>,
  i18nKey: string | null,
  locale: NavLocale,
  t: Translate,
): string {
  const own = label?.[locale]
  if (own && own.trim().length > 0) return own
  if (i18nKey) {
    // tCms-style fallback: never show the raw key to a visitor.
    const translated = t(i18nKey)
    if (translated && translated !== i18nKey) return translated
  }
  // No i18n key and no operator label — better a readable path than a blank.
  return label?.[locale]?.trim() || i18nKey || ''
}

function intOr(value: unknown, fallback: number): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback
}

/**
 * Whether the site has at least one published article.
 *
 * The articles nav item is hidden entirely when this is false, so a site with
 * no article content never shows a "文章分享" link that leads to an empty list.
 * Mirrors the title filter in `collectionChildren` so "has articles" and
 * "dropdown has items" can never disagree.
 */
export function hasArticles(articles?: CmsArticleIndexEntry[]): boolean {
  const source = articles ?? cmsArticles
  return source.some((a) => a.title.trim().length > 0)
}

/**
 * Whether a nav node is THE ARTICLES ENTRY.
 *
 * The articles entry used to be a `collection` node — a dropdown of the latest
 * three posts. On 2026-11-03 the operator asked for it to be a plain link that
 * goes straight to the articles page, so it can no longer be recognised by
 * `link_kind`: a plain internal link to /articles is exactly what it has
 * become, and keying off the kind would stop recognising it.
 *
 * So the entry is identified by its i18n key, or by what it POINTS AT (its
 * href). Two rules hang off this:
 *
 *  - the entry is hidden entirely when the site has no articles (hasArticles),
 *  - and it never derives a dropdown, whatever the CMS row says.
 *
 * Note it is NOT identified by `source === 'articles'`: that would also catch
 * any other node an operator might build as a "latest posts" submenu
 * somewhere else in the tree, and that one should keep its dropdown.
 *
 * The href test is deliberately narrow — `/articles` and `/articles/` only. A
 * sub-link to one article (`/articles/some-slug`) is a different thing and
 * must keep behaving like any other link.
 */
function isArticlesNode(node: NavItem): boolean {
  if (node.i18n_key === 'nav.articles') return true
  const href = node.href?.trim().toLowerCase()
  return href === '/articles' || href === '/articles/'
}

/** Auto-derive a submenu from a content collection. The derived entries are
 *  always leaves — a collection item's submenu is a flat list of content, not
 *  another level of menu. */
function collectionChildren(
  source: string,
  filter: NavSourceFilter,
  t: Translate,
  data?: NavData,
): ResolvedNavNode[] {
  const limit = intOr(filter.limit, 3)

  if (source === 'articles') {
    const wanted = typeof filter.template_key === 'string' ? filter.template_key : null
    const rows = (data?.articles ?? cmsArticles)
      .filter((a) => a.title.trim().length > 0)
      .filter((a) => (wanted ? a.template_key === wanted : true))
      .slice(0, limit)
    return rows.map((a) => {
      const cover = a.cover_media_uuid ? (data?.mediaIndex ?? cmsMediaIndex)[a.cover_media_uuid] : undefined
      return {
        key: `article-${a.slug}`,
        label: a.title,
        href: `/articles/${a.slug}`,
        isExternal: false,
        openNewTab: false,
        children: [],
        autoChildren: false,
        collectionHref: null,
        thumb: cover?.urls.thumb ?? cover?.urls.medium ?? cover?.urls.original,
        thumbSrcSet: cmsMediaSrcSet(a.cover_media_uuid, data?.mediaIndex ?? cmsMediaIndex),
      }
    })
  }

  if (source === 'lectures') {
    const audience = typeof filter.audience === 'string' ? filter.audience : null
    const rows = (data?.lectures ?? cmsLectures)
      .filter((l) => (audience ? l.audience === audience : true))
      .slice(0, limit)
    return rows.map((l) => ({
      key: `lecture-${l.slug}`,
      // The generated index is locale-collapsed to zh-Hant with an `title`
      // field (see public.ts pickMagLocale), so there is no per-locale title
      // here — the detail page handles the rest.
      label: l.title || l.slug,
      href: `/lectures/${l.slug}`,
      isExternal: false,
      openNewTab: false,
      children: [],
      autoChildren: false,
      collectionHref: null,
      thumb: l.photo1_url || undefined,
      // A lecture photo is a media-library upload, so the ladder can serve
      // the 48px box a 320px rung instead of the original file.
      thumbSrcSet: cmsMediaSrcSet(uuidFromUploadPath(l.photo1_url), data?.mediaIndex ?? cmsMediaIndex),
    }))
  }

  if (source === 'magazines') {
    const kind = typeof filter.magazine_kind === 'string' ? filter.magazine_kind : null
    const rows = (data?.magazines ?? cmsMagazines)
      .filter((m) => (kind ? m.kind === kind : true))
      .slice(0, limit)
    return rows.map((m) => ({
      key: `magazine-${m.slug}`,
      label: m.title || m.issue || m.slug,
      href: `/magazine/${m.slug}`,
      isExternal: false,
      openNewTab: false,
      children: [],
      autoChildren: false,
      collectionHref: null,
      thumb: m.cover_media_uuid ? (data?.mediaIndex ?? cmsMediaIndex)[m.cover_media_uuid]?.urls.medium : undefined,
      thumbSrcSet: cmsMediaSrcSet(m.cover_media_uuid, data?.mediaIndex ?? cmsMediaIndex),
    }))
  }

  void t
  return []
}

/** Where a collection item's "view all" link goes. */
function collectionLanding(source: string | null, href: string | null): string | null {
  if (href) return href
  if (source === 'articles') return '/articles'
  if (source === 'lectures') return '/lectures'
  if (source === 'magazines') return '/magazine'
  return null
}

/**
 * Resolve one node and, recursively, its whole subtree.
 *
 * `depth` is the node's own depth (0 for a top-level link). The recursion
 * stops at NAV_MAX_DEPTH: the CMS will not store a deeper row, but the
 * snapshot it hands us could in principle carry one, and rendering it would
 * mean rendering a level the header has no room for.
 */
function resolveNode(
  node: NavItem,
  locale: NavLocale,
  t: Translate,
  depth: number,
  data?: NavData,
): ResolvedNavNode {
  const isExternal = node.link_kind === 'external'
  const href = isExternal ? (node.href ?? '') : (node.href ?? '/')
  const base = {
    key: `nav-${node.id}`,
    label: resolveLabel(node.label, node.i18n_key, locale, t),
    href,
    isExternal,
    openNewTab: node.open_new_tab,
  }

  // The articles entry is a DIRECT LINK (2026-11-03). It used to derive a
  // "latest 3 posts" dropdown from the articles collection; the operator
  // asked for the menu entry to go straight to /articles instead.
  //
  // This is checked BEFORE both child sources, so it holds whether the CMS
  // row still says `collection` (the operator has not flipped it yet) or has
  // already been changed to a plain internal link. The CMS row is expected to
  // follow — scripts/set-nav-articles-plain-link.mts does it — but the menu
  // behaves as asked from the moment this code ships.
  if (isArticlesNode(node)) {
    return {
      ...base,
      children: [],
      autoChildren: false,
      collectionHref: null,
    }
  }

  if (node.children.length > 0) {
    return {
      ...base,
      children:
        depth >= NAV_MAX_DEPTH
          ? []
          : node.children.map((child) => resolveNode(child, locale, t, depth + 1, data)),
      autoChildren: false,
      collectionHref: null,
    }
  }

  if (node.link_kind === 'collection' && node.source) {
    return {
      ...base,
      children: collectionChildren(node.source, node.source_filter, t, data),
      autoChildren: true,
      collectionHref: collectionLanding(node.source, node.href),
    }
  }

  return {
    ...base,
    children: [],
    autoChildren: false,
    collectionHref: null,
  }
}

/**
 * Resolve a nav TREE into its renderable shape.
 *
 * Split out from `resolveNav` so it can be handed a SYNTHETIC tree. The live
 * snapshot has no third level until an operator builds one, so the only way
 * to prove the third level survives resolution is to give the resolver a tree
 * that has one — which is exactly what scripts/test-nav-resolver.mts does.
 *
 * `resolveNav` is the only caller that reads the live snapshot.
 */
export function resolveNavTree(
  tree: NavItem[],
  locale: NavLocale,
  t: Translate,
  data?: NavData,
): ResolvedNavNode[] {
  return tree
    // Drop the articles entry up front when there is nothing to show; see
    // hasArticles. Filtering the source node (rather than the resolved item)
    // keeps the label-based filter below as the only thing that decides
    // whether a labelled node survives.
    //
    // The test is `isArticlesNode`, not "link_kind === collection": the entry
    // is a plain link now, and a plain link to an empty article list is just
    // as bad as a dropdown full of nothing.
    .filter((node) => !(isArticlesNode(node) && !hasArticles(data?.articles)))
    .map((node) => resolveNode(node, locale, t, 0, data))
    // A node whose label resolves to nothing renders as a zero-width,
    // invisible <a> — worse than absent, because it is still a focusable
    // tab stop that a screen reader announces as an unlabelled link.
    //
    // The label alone is the test. Testing `label || href` looks equivalent
    // but is not: `resolveNode` defaults an internal node's href to '/', so
    // the href branch was always truthy and a node with an empty label and
    // an i18n_key that does not exist still slipped through. That is exactly
    // what nav item id=7 (href '/', no label, no i18n_key) did.
    .filter((item) => item.label.trim().length > 0)
}

/**
 * The site's navigation for `locale`.
 *
 * Falls back to DEFAULT_NAV_SEEDS when the CMS has no visible items. See the
 * note on that constant for why this fallback exists.
 *
 * `navItems` is optional: when omitted, the build-time `cmsNav` snapshot is
 * used (the SSR/prerender path). When supplied — e.g. from `useCmsNav()` at
 * runtime — that list is used instead, so a menu edit in the CMS shows
 * without a rebuild.
 */
export function resolveNav(
  locale: NavLocale,
  t: Translate,
  navItems?: NavItem[],
  data?: NavData,
): ResolvedNavItem[] {
  const source = navItems ?? cmsNav
  if (source.length > 0) {
    return resolveNavTree(source, locale, t, data)
  }

  return DEFAULT_NAV_SEEDS
    // Same rule as the CMS branch: no articles, no articles nav item.
    .filter((seed) => !(seed.key === 'articles' && !hasArticles(data?.articles)))
    .map((seed) => ({
      key: `default-${seed.key}`,
      label: t(seed.labelKey),
      href: seed.href,
      isExternal: false,
      openNewTab: false,
      children: [],
      autoChildren: false,
      collectionHref: null,
    }))
}